import React from 'react';
import { PermissionsProvider } from 'app/providers/PermissionsProvider';
import { ConfigProvider } from 'app/providers/ConfigProvider';
import { TierProvider } from 'app/providers/TierProvider';
import { ThemeProvider } from 'app/providers/ThemeProvider';
import { QuoteActionsProvider } from 'app/providers/QuoteActionsProvider';
import { SectionStatusProvider } from 'app/providers/SectionStatusProvider';
import { LayoutProvider, useLayout } from 'app/shell/LayoutProvider';
import { Workbench, type WorkbenchProps } from 'app/shell/Workbench';
import { Spine } from 'app/shell/Spine';
import { Nameplate } from 'app/shell/Nameplate';
import { RailFrame } from 'app/shell/RailFrame';
import { RibbonChips } from 'app/shell/RibbonChips';
import { useVisibleSections } from 'app/registry/sectionRegistry';
import { useVisibleRailTiles } from 'app/registry/railRegistry';
import { Notices } from 'features/notices';
import { LiveRail, useLiveRail } from 'features/live-rail';
import { SizingModeToggle } from 'features/storage-set';
import { RackRail } from 'features/rack-view';
import { Toasts } from 'features/toasts';
import { useQuoteActionsCtx } from 'shared/state/quoteActionsContext';
import BusyOverlay from 'components/BusyOverlay';
import { Overlays as OverlayHost } from 'app/shell/Overlays';
import type { AppUser } from 'shared/state/types';

export type AppShellUser = AppUser;
/** initialVendorId: the user's first accessible vendor, decided by ConfiguratorGate
 *  before this mounts. ConfigProvider's one useConfigurationLogic call captures its
 *  initial config on the first render, so the shell must start on the right vendor. */
export type AppShellProps = { user: AppShellUser; signOut: () => void; initialVendorId?: string };

function Sections() {
  const sections = useVisibleSections();
  return <>{sections.map((s) => <s.Component key={s.id} id={s.id} />)}</>;
}

function Rail() {
  const { ctx } = useLiveRail();
  const tiles = useVisibleRailTiles(ctx);
  // The sizing mode governs both storage sets, so it sits with the system-wide
  // controls in the stat block. Passed as a slot: features do not import each other.
  // The compact rack beside the stat block is an A/B trial against the Rack section
  // in the workbench (2026-10-04); both are drawn until one is chosen.
  return <LiveRail tiles={tiles} controls={<SizingModeToggle />} beside={<RackRail />} />;
}

function Overlays() {
  const { busy, execConfirmElement } = useQuoteActionsCtx();
  return <><BusyOverlay label={busy} />{execConfirmElement}<OverlayHost /><Toasts /></>;
}

/** Workbench is layout-only and takes the pin as a prop; this is where the
 *  layout service (which the Spine's pin button toggles) is read for it. It has
 *  to be a component of its own because LayoutProvider is mounted by AppShell
 *  itself, so AppShell cannot call useLayout. */
function PinnedWorkbench(props: WorkbenchProps) {
  const { spinePinned } = useLayout();
  return <Workbench {...props} navPinned={spinePinned} />;
}

/** Composition root of the V2 shell. Providers own state; registries own the lists; Workbench owns layout. */
export function AppShell({ user, signOut, initialVendorId }: AppShellProps) {
  return (
    <PermissionsProvider user={user}>
      <ConfigProvider initialVendorId={initialVendorId}>
        <TierProvider>
          <ThemeProvider>
            <LayoutProvider>
              <SectionStatusProvider>
                <QuoteActionsProvider user={user}>
                  <PinnedWorkbench
                    nav={<Spine />}
                    topBar={<Nameplate user={user} signOut={signOut} />}
                    headline={<RibbonChips />}
                    notices={<Notices />}
                    main={<Sections />}
                    rail={<RailFrame><Rail /></RailFrame>}
                    // The overlays slot hosts BusyOverlay, the exec confirm dialog, the
                    // Rack View / System Slide host and the save/load/export/reprice
                    // overlays (all registered on OVERLAYS, Phase 5/6), plus the toast
                    // stack (Phase 6) — so failures raised through showToast (save,
                    // generate, reprice) now surface on this route. The keyboard
                    // shortcuts (useKeyboardShortcuts + ShortcutSheet) are not mounted:
                    // switched off 2026-10-04, the modules are kept for a later return.
                    overlays={<Overlays />}
                  />
                </QuoteActionsProvider>
              </SectionStatusProvider>
            </LayoutProvider>
          </ThemeProvider>
        </TierProvider>
      </ConfigProvider>
    </PermissionsProvider>
  );
}

export default AppShell;
